import { useState } from "react";
import { DropshipProduct } from "@/mocks/dropship";

interface Props {
  product: DropshipProduct;
  onClose: () => void;
  onOrderPlaced: (order: PlacedOrder) => void;
  isLoggedIn: boolean;
  onLoginRequired: () => void;
}

export interface PlacedOrder {
  id: string;
  productId: number;
  productName: string;
  productImage: string;
  sellerName: string;
  qty: number;
  unitPrice: number;
  shippingFee: number;
  total: number;
  status: "Pending" | "Processing" | "Shipped" | "Delivered";
  trackingNumber: string;
  warehouseAddress: string;
  deliveryAddress: string;
  date: string;
  confirmed: boolean;
}

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming",
];

// UPS shipping rate calculator (simplified)
function calcUPSShipping(weightLbs: number, state: string, qty: number): { rate: number; service: string; days: string } {
  const totalWeight = weightLbs * qty;
  const westCoast = ["California", "Oregon", "Washington", "Nevada", "Arizona"];
  const midwest = ["Illinois", "Ohio", "Michigan", "Indiana", "Wisconsin", "Minnesota"];
  const southeast = ["Florida", "Georgia", "North Carolina", "South Carolina", "Tennessee"];
  const northeast = ["New York", "New Jersey", "Massachusetts", "Connecticut", "Pennsylvania"];

  let baseRate = 8.99;
  if (westCoast.includes(state)) baseRate = 7.99;
  else if (midwest.includes(state)) baseRate = 9.49;
  else if (southeast.includes(state)) baseRate = 10.49;
  else if (northeast.includes(state)) baseRate = 9.99;

  const weightSurcharge = Math.max(0, (totalWeight - 1) * 1.5);
  const rate = parseFloat((baseRate + weightSurcharge).toFixed(2));

  let service = "UPS Ground";
  let days = "3–5 business days";
  if (westCoast.includes(state) || northeast.includes(state)) {
    service = "UPS 2nd Day Air";
    days = "2 business days";
  }

  return { rate, service, days };
}

// Approximate product weights in lbs
function getProductWeight(category: string): number {
  const weights: Record<string, number> = {
    "Electronics": 0.8,
    "Health & Beauty": 0.4,
    "Home & Garden": 1.5,
    "Sports & Fitness": 2.0,
    "Apparel": 0.6,
    "Auto Parts": 3.0,
    "Food & Beverage": 1.2,
  };
  return weights[category] || 1.0;
}

export default function OrderModal({ product, onClose, onOrderPlaced, isLoggedIn, onLoginRequired }: Props) {
  const [step, setStep] = useState<"details" | "shipping" | "confirm" | "success">("details");
  const [qty, setQty] = useState(1);
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("California");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [payMethod, setPayMethod] = useState<"card" | "paypal">("card");
  const [cardNum, setCardNum] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [error, setError] = useState("");

  const weight = getProductWeight(product.category);
  const shipping = calcUPSShipping(weight, state, qty);
  const subtotal = parseFloat((product.price * qty).toFixed(2));
  const total = parseFloat((subtotal + shipping.rate).toFixed(2));

  const handleShippingNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!fullName || !address || !city || !zip || !phone) {
      setError("Please fill in all required fields.");
      return;
    }
    setStep("confirm");
  };

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) { onLoginRequired(); return; }
    if (payMethod === "card" && (!cardNum || !cardExp || !cardCvv)) {
      setError("Please enter your card details.");
      return;
    }
    const trackingNum = `1Z${Math.random().toString(36).substring(2, 9).toUpperCase()}${Math.floor(Math.random() * 9000 + 1000)}`;
    const order: PlacedOrder = {
      id: `#DS-${Math.floor(9000 + Math.random() * 999)}`,
      productId: product.id,
      productName: product.name,
      productImage: product.image,
      sellerName: product.seller,
      qty,
      unitPrice: product.price,
      shippingFee: shipping.rate,
      total,
      status: "Pending",
      trackingNumber: trackingNum,
      warehouseAddress: `${product.sellerFlag} ${product.sellerCountry} → ${state} Warehouse`,
      deliveryAddress: `${address}, ${city}, ${state} ${zip}`,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      confirmed: false,
    };
    onOrderPlaced(order);
    setStep("success");
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step !== "details" && step !== "success" && (
              <button onClick={() => setStep(step === "confirm" ? "shipping" : "details")} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-arrow-left-line"></i>
              </button>
            )}
            <h2 className="font-extrabold text-gray-900 text-base">
              {step === "details" && "Product Details"}
              {step === "shipping" && "Shipping Information"}
              {step === "confirm" && "Confirm & Pay"}
              {step === "success" && "Order Placed!"}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Step indicator */}
        {step !== "success" && (
          <div className="flex items-center px-6 py-3 gap-2">
            {["details", "shipping", "confirm"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  step === s ? "bg-amber-400 text-white" :
                  ["details", "shipping", "confirm"].indexOf(step) > i ? "bg-green-400 text-white" : "bg-gray-100 text-gray-400"
                }`}>
                  {["details", "shipping", "confirm"].indexOf(step) > i ? <i className="ri-check-line text-xs"></i> : i + 1}
                </div>
                <span className={`text-xs font-semibold capitalize ${step === s ? "text-amber-500" : "text-gray-400"}`}>{s}</span>
                {i < 2 && <div className="flex-1 h-px bg-gray-200"></div>}
              </div>
            ))}
          </div>
        )}

        <div className="px-6 pb-6">
          {/* Step 1: Product details */}
          {step === "details" && (
            <div>
              <div className="flex gap-4 mb-5 mt-3">
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{product.category}</span>
                  <h3 className="font-bold text-gray-900 text-sm mt-1.5 mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{product.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-400">{product.sellerFlag} {product.seller}</span>
                    <span className="text-xs text-green-600 font-semibold"><i className="ri-stack-line mr-0.5"></i>{product.stock.toLocaleString()} in stock</span>
                  </div>
                </div>
              </div>

              {/* Warehouse info */}
              <div className="bg-amber-50 rounded-xl p-3 mb-4 flex items-center gap-2">
                <i className="ri-building-2-line text-amber-500"></i>
                <div>
                  <p className="text-xs font-semibold text-amber-700">Ships from Overseas Warehouse</p>
                  <p className="text-xs text-amber-600">{product.sellerCountry} → Nearest US Warehouse → Your Door</p>
                </div>
              </div>

              {/* Quantity */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm font-semibold text-gray-700">Quantity</span>
                <div className="flex items-center border border-gray-200 rounded-full overflow-hidden bg-white">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors text-lg">-</button>
                  <span className="px-5 text-sm font-bold text-gray-800">{qty}</span>
                  <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors text-lg">+</button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Unit Price</span>
                  <span className="font-semibold text-gray-800">${product.price}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Subtotal ({qty} unit{qty > 1 ? "s" : ""})</span>
                  <span className="font-semibold text-gray-800">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400 mb-3">
                  <span>UPS Shipping</span>
                  <span>Calculated next step</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-bold text-gray-800">Est. Total</span>
                  <span className="font-extrabold text-amber-500 text-lg">${subtotal.toFixed(2)}+</span>
                </div>
              </div>

              <button
                onClick={() => setStep("shipping")}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue to Shipping <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          )}

          {/* Step 2: Shipping */}
          {step === "shipping" && (
            <form onSubmit={handleShippingNext} className="mt-3">
              <div className="flex flex-col gap-3.5">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
                  <input required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Street Address *</label>
                  <input required value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main Street, Apt 4B" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">City *</label>
                    <input required value={city} onChange={e => setCity(e.target.value)} placeholder="Los Angeles" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">ZIP Code *</label>
                    <input required value={zip} onChange={e => setZip(e.target.value)} placeholder="90001" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">State *</label>
                  <select
                    value={state}
                    onChange={e => setState(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                  >
                    {US_STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number *</label>
                  <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>

                {/* UPS Shipping estimate */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-truck-line text-amber-500"></i>
                    <span className="text-sm font-bold text-amber-700">UPS Shipping Estimate</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{shipping.service}</span>
                    <span className="font-bold text-amber-600">${shipping.rate}</span>
                  </div>
                  <p className="text-xs text-gray-500">Estimated delivery: {shipping.days} to {state}</p>
                  <p className="text-xs text-gray-400 mt-1">Weight: ~{(weight * qty).toFixed(1)} lbs · {qty} unit{qty > 1 ? "s" : ""}</p>
                </div>

                {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

                <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  Continue to Payment <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Confirm & Pay */}
          {step === "confirm" && (
            <form onSubmit={handlePlaceOrder} className="mt-3">
              {/* Order summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Order Summary</h4>
                <div className="flex gap-3 mb-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-2">{product.name}</p>
                    <p className="text-xs text-gray-400">Qty: {qty} × ${product.price}</p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3 flex flex-col gap-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">UPS {shipping.service}</span>
                    <span className="font-semibold">${shipping.rate}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-200 mt-1">
                    <span>Total</span>
                    <span className="text-amber-500 text-base">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery address */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Delivery To</h4>
                <p className="text-sm font-semibold text-gray-800">{fullName}</p>
                <p className="text-sm text-gray-600">{address}</p>
                <p className="text-sm text-gray-600">{city}, {state} {zip}</p>
                <p className="text-sm text-gray-500">{phone}</p>
              </div>

              {/* Payment method */}
              <div className="mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Payment Method</h4>
                <div className="flex gap-2 mb-3">
                  <button type="button" onClick={() => setPayMethod("card")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${payMethod === "card" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}>
                    <i className="ri-bank-card-line"></i> Credit Card
                  </button>
                  <button type="button" onClick={() => setPayMethod("paypal")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all ${payMethod === "paypal" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}>
                    <i className="ri-paypal-line"></i> PayPal
                  </button>
                </div>

                {payMethod === "card" && (
                  <div className="flex flex-col gap-3">
                    <input value={cardNum} onChange={e => setCardNum(e.target.value)} placeholder="Card number (1234 5678 9012 3456)" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={19} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={cardExp} onChange={e => setCardExp(e.target.value)} placeholder="MM/YY" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={5} />
                      <input value={cardCvv} onChange={e => setCardCvv(e.target.value)} placeholder="CVV" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" maxLength={4} />
                    </div>
                  </div>
                )}
                {payMethod === "paypal" && (
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <i className="ri-paypal-line text-3xl text-amber-500 mb-2 block"></i>
                    <p className="text-sm text-gray-600">You will be redirected to PayPal to complete payment.</p>
                  </div>
                )}
              </div>

              {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

              <button type="submit" className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base">
                <i className="ri-lock-line mr-2"></i>Place Order — ${total.toFixed(2)}
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">Secured by SSL encryption</p>
            </form>
          )}

          {/* Success */}
          {step === "success" && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Order Placed Successfully!</h3>
              <p className="text-gray-500 text-sm mb-5">Your order is confirmed. You can track it in your dashboard.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 w-full mb-5">
                <p className="text-xs text-amber-600 font-semibold mb-1">UPS Tracking Number</p>
                <p className="text-base font-extrabold text-gray-900 font-mono tracking-wider">Auto-assigned on shipment</p>
                <p className="text-xs text-gray-400 mt-1">You will receive tracking info via email/SMS once shipped</p>
              </div>
              <button onClick={onClose} className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                View My Orders
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
